'use client';

import React from 'react';
import Image from 'next/image';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { maybeCacheBust, toAbsoluteMediaUrl } from '@/lib/media';
import { profileThumb } from '@/lib/imageUrls';
import { useInView } from '@/hooks/useInView';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import { Heart, Lock } from 'lucide-react';
import { scrubContactInfo } from '@/lib/scrubContact';
import { normalizeSlug } from '@/lib/seo/slug';
import ProducerBadge from '@/components/common/ProducerBadge';
import { cardProducerOf } from '@/lib/producerLabel';

export interface TalentCardProps {
  id: string | number;
  stageName: string;
  profileImageLocation?: string;
  /** Full image URL fallback when no key is available */
  avatarUrl?: string;
  /** ISO date string like "1990-02-10" */
  age?: string;
  /** full "male"/"female" */
  sex?: string;
  locationName?: string;
  /** Optional state code (e.g., "CA") */
  state?: string;
  /** Optional metro slug (e.g., "los-angeles") */
  metro?: string;
  /** When true, eagerly load image for LCP */
  eager?: boolean;
  /** overlay style */
  variant?: 'brand' | 'legacy' | 'default';
  /** size for typography/padding */
  size?: 'sm' | 'md';
  /** surface treatment */
  surface?: 'default' | 'light';
  /** optional small badges/icons on top-right */
  badges?: React.ReactNode;
  /** when true, show verified badge */
  isVerified?: boolean;
  /** total likes received across all media (profile + posts + galleries + videos). Renders a heart chip when > 0. */
  likeCount?: number;
  /** @deprecated Ignored. Gating is server-side; API picks the correct avatar URL. */
  unsafe?: boolean;
  /** @deprecated Ignored. Gating is server-side. */
  gated?: boolean;
  /** @deprecated Ignored. API returns the final avatar URL (blurred or original). */
  blurredAvatarUrl?: string;
  /**
   * The public Producer label from the card DTO (the api's label rule). Shown only when this is
   * true and producerRole is a role value; a talent card sends false and nulls and is unchanged.
   */
  isProducer?: boolean;
  /** "producer" or "producer_and_talent", null unless isProducer. */
  producerRole?: string | null;
  /** The producer's business name, null unless isProducer. */
  producerBusinessName?: string | null;
  /**
   * "producer" is the producers list card: the Producer badge and the business name take the
   * place of the age, sex and city line. The default talent card keeps that line and adds the
   * badge under it for a member who is a producer and talent.
   */
  kind?: 'talent' | 'producer';
}

export default function TalentCard({
  id,
  stageName,
  profileImageLocation,
  avatarUrl,
  age,
  sex,
  locationName,
  state: stateCode,
  eager = false,
  variant = 'brand',
  size = 'md',
  surface = 'default',
  badges,
  isVerified = false,
  likeCount,
  isProducer,
  producerRole,
  producerBusinessName,
  kind = 'talent',
}: TalentCardProps) {
  const producer = cardProducerOf({ isProducer, producerRole, producerBusinessName });
  // Simplified image resolution: prefer avatarUrl (pre-computed with _thumb), fallback to profileImageLocation
  const candidates = React.useMemo(() => {
    const out: string[] = [];
    // avatarUrl is already resolved to absolute with _thumb variant via mapPersonToTalent
    if (avatarUrl) {
      out.push(String(avatarUrl));
    }
    // Fallback: if profileImageLocation provided as key, resolve it
    if (profileImageLocation && profileImageLocation !== avatarUrl) {
      out.push(profileThumb(String(profileImageLocation)));
    }
    // Final fallback is handled in onError
    return out.filter(Boolean);
  }, [avatarUrl, profileImageLocation]);

  const [imgIdx, setImgIdx] = React.useState(0);
  const primary = candidates[imgIdx];

  // Initialize with the same value that useEffect will set to avoid hydration mismatch
  const [imgSrc, setImgSrc] = React.useState<string | undefined>(() => {
    if (!primary) return undefined;
    // API sends correct blurred URLs for guests - just use as-is
    return maybeCacheBust(primary);
  });

  // Keep imgSrc in sync if the incoming primary URL changes
  React.useEffect(() => {
    if (!primary) {
      setImgSrc(undefined);
      return;
    }
    // API sends correct blurred URLs for guests - just use as-is
    setImgSrc(maybeCacheBust(primary));
  }, [primary]);

  // Reset candidate index when the candidate list changes
  React.useEffect(() => {
    setImgIdx(0);
  }, [candidates.length, candidates[0]]);

  // compute numeric age from ISO date string or numeric age
  const shownAge = React.useMemo(() => {
    if (age == null) return null;
    // Accept direct numeric ages, numeric strings, or ISO DOB strings
    const s = String(age).trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isNaN(n) && Number.isFinite(n) && n > 0 && n < 120) {
      return Math.floor(n);
    }
    // Try parsing as ISO date string (DOB)
    const dob = new Date(s);
    if (isNaN(dob.getTime())) return null;
    const diff = Date.now() - dob.getTime();
    return Math.max(0, new Date(diff).getUTCFullYear() - 1970);
  }, [age]);
  const { ref: viewRef } = useInView<HTMLAnchorElement>({ rootMargin: '400px', once: true });

  // normalize sex label (Male/Female/Other)
  const niceSex = sex ? (sex.charAt(0).toUpperCase() + sex.slice(1)) : undefined;
  const initials = React.useMemo(
    () => (stageName || '')
      .trim()
      .split(/\s+/)
      .map((s) => s.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || '??',
    [stageName]
  );

  // Style variants — unified with the rest of the site (rounded-2xl, soft shadow, subtle border).
  const cardBase = 'relative overflow-hidden p-0 rounded-2xl border border-zinc-200/60 dark:border-zinc-800 transition-shadow duration-200';
  const cardVariant = 'shadow-sm hover:shadow-md';
  // Stronger mid-stop: /40 washed out on bright photos once group-hover
  // brightness-110 kicked in, leaving the white name text illegible.
  const gradientCls = 'from-black/85 via-black/55';
  const displayLocation = React.useMemo(() => {
    if (!locationName) return '';
    const s = String(locationName);
    const idx = s.indexOf(',');
    return idx > 0 ? s.slice(0, idx).trim() : s;
  }, [locationName]);
  // Tightened typography to match the post/profile redesign — serif name, clean meta line.
  const titleCls = (size === 'sm' ? 'text-base' : 'text-lg');
  const metaCls = 'text-xs sm:text-sm';
  const cardSurface = '';

  const slug = normalizeSlug(stageName);
  const profileHref = slug
    ? `/u/${slug}`
    : `/details/${encodeURIComponent(String(id))}`;

  return (
    <a
      itemScope
      itemType="https://schema.org/Person"
      ref={viewRef}
      href={profileHref}
      className="group block cursor-pointer rounded-lg focus-ring"
      aria-label={`View profile for ${stageName}${shownAge != null ? ', age ' + shownAge : ''}`}
      // no observer/presign; fail fast and show placeholder if not available
    >
      <Card className={cn(cardBase, cardVariant, cardSurface)}>
        {/* Like count chip (top-left) — matches the heart indicator on the profile page */}
        {typeof likeCount === 'number' && likeCount > 0 ? (
          <div
            className="absolute top-2 left-2 z-20 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white"
            aria-label={`${likeCount} ${likeCount === 1 ? 'like' : 'likes'}`}
          >
            <Heart className="h-3 w-3 fill-current" aria-hidden="true" />
            <span>{likeCount}</span>
          </div>
        ) : null}
        {/* Badges (top-right) */}
        {badges ? (
          <div className="absolute top-2 right-2 z-20 flex gap-1">{badges}</div>
        ) : /_blurred/i.test(String(imgSrc || '')) ? (
          // Guest-gated media: the server sent the blurred variant. Without a
          // label the blur reads as a broken image, not a membership perk.
          // Presentation only — the gating decision stays server-side. Not a
          // link: the whole card is already an <a> to the profile, where the
          // join pill lives.
          <span className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white">
            <Lock className="h-3 w-3" aria-hidden="true" />
            <span>Join free to view</span>
          </span>
        ) : null}

        {/* Image layer — NO client-side gating; API returns correct URL per viewer */}
        <div className="relative w-full aspect-[3/4]">
          {imgSrc ? (
            <Image
              src={imgSrc}
              alt={stageName || 'Profile photo'}
              itemProp="image"
              fill
              className="absolute inset-0 h-full w-full object-cover transition-all duration-300 group-hover:scale-105 group-hover:brightness-110"
              sizes="(min-width: 1536px) 16vw, (min-width: 1280px) 16vw, (min-width: 1024px) 20vw, (min-width: 768px) 25vw, 50vw"
              priority={!!eager}
              loading={eager ? 'eager' : 'lazy'}
              onError={() => {
                // Try next candidate if available
                if (imgIdx + 1 < candidates.length) {
                  setImgIdx(imgIdx + 1);
                  return;
                }
                // Final fallback to default avatar
                setImgSrc('/img/person.svg');
              }}
            />
          ) : (
            <Image
              src="/img/person.svg"
              alt={stageName || 'Profile photo'}
              itemProp="image"
              fill
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              sizes="(min-width: 1536px) 16vw, (min-width: 1280px) 16vw, (min-width: 1024px) 20vw, (min-width: 768px) 25vw, 50vw"
              priority={!!eager}
              loading={eager ? 'eager' : 'lazy'}
            />
          )}

          {/* Full-image overlay */}
          <>
            <div className={cn('absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t to-transparent', gradientCls)} aria-hidden="true" />
            <div className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-2 md:px-5 md:pb-5">
              <div className="max-w-[95%]">
                <h3 className={cn('font-serif font-semibold tracking-tight truncate text-white flex items-center gap-1.5 leading-tight', titleCls)}>
                  <span className="truncate">{scrubContactInfo(stageName)}</span>
                  {isVerified && <VerifiedBadge size={14} />}
                </h3>
                {producer && kind === 'producer' ? (
                  // Producer card: the badge and the business name in place of the stats line.
                  <p className={cn('mt-1 flex min-w-0 items-center gap-1.5 text-white/85 font-medium', metaCls)} data-testid="producer-card-line">
                    <ProducerBadge role={producer.role} tone="dark" className="shrink-0" />
                    {producer.businessName ? <span className="truncate">{scrubContactInfo(producer.businessName)}</span> : null}
                  </p>
                ) : (
                <p className={cn('mt-0.5 truncate text-white/85 font-medium', metaCls)}>
                  {([shownAge != null ? String(shownAge) : undefined, niceSex, displayLocation ? scrubContactInfo(displayLocation) : undefined].filter(Boolean) as string[]).join(' \u00B7 ')}
                </p>
                )}
                {producer && kind !== 'producer' ? (
                  // Talent card of a producer and talent: the badge on its own line under the
                  // stats, apart from the Verified check beside the name.
                  <div className="mt-1.5 flex">
                    <ProducerBadge role={producer.role} tone="dark" />
                  </div>
                ) : null}
              </div>
            </div>
          </>

        </div>
      </Card>
      {/* Person microdata properties */}
      <meta itemProp="name" content={stageName} />
      {niceSex ? <meta itemProp="gender" content={niceSex} /> : null}
      {(displayLocation || stateCode) ? (
        <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress" className="sr-only">
          {displayLocation ? <meta itemProp="addressLocality" content={displayLocation} /> : null}
          {stateCode ? <meta itemProp="addressRegion" content={String(stateCode)} /> : null}
        </span>
      ) : null}
    </a>
  );
}
